import React, { useState, useEffect, FC, useMemo, useRef } from 'react';
import './stock-table.scss';
import { Button } from 'primereact/button';
import StockTableRow, { StockTableRowProps } from '../stock-table-row/stock-table-row';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { useSelector, useDispatch } from 'react-redux';
import StockTableCountCell, { StockDestination } from '../stock-table-count-cell/stock-table-count-cell';
import { updateICProductsByShop } from '../../productSlices';
// Add these types at the top of the file
type SortField = 'name' | 'seven_liter' | 'five_liter' | 'three_liter' | 'total_quantity';
type SortOrder = 'asc' | 'desc';

const StockTable = ({ 
  showUpperControls = true, 
  search,
  setSearch,
  showFilters,
  setShowFilters,
  selectedFilters,
  setSelectedFilters,
  handleStockModify,
  modifiedProducts,
  hasModifications,
  onShowModifications,
  ...props 
}) => {
    const ic_products = useSelector((state: any) => state.product.ic_products);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 7;
    const [checkedStocks, setCheckedStocks] = useState([]);
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, ic_products.length);
    const selectedShop = useSelector((state: any) => state.user.selectedShop);
    const user = useSelector((state: any) => state.auth.user);
    const userAllowedToEdit = user.roleLevel >= 2;
    const dispatch:Function = useDispatch();
    const bulkLoading = useSelector((state: any) => state.product.bulk_loading);
    // Add new state for sorting
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [destinationShop, setDestinationShop] = useState<string | null>(null);    
    // Add new state for screen width
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    // Update the filteredStock memo to include sorting
    const filteredStock = useMemo(() => {
        const filtered = ic_products.filter(stock => {
            const matchesSearch = stock.name.toLowerCase().includes(search.toLowerCase());
            const matchesFilters = selectedFilters.length === 0 || selectedFilters.includes(stock.name);
            return matchesSearch && matchesFilters;
        });

        return [...filtered].sort((a, b) => {
            let comparison = 0;
            switch (sortField) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'seven_liter':
                    comparison = parseFloat(a.seven_liter || '0') - parseFloat(b.seven_liter || '0');
                    break;
                case 'five_liter':
                    comparison = parseFloat(a.five_liter || '0') - parseFloat(b.five_liter || '0');
                    break;
                case 'three_liter':
                    comparison = parseFloat(a.three_liter || '0') - parseFloat(b.three_liter || '0');
                    break;
                case 'total_quantity':
                    comparison = parseFloat(a.total_quantity || '0') - parseFloat(b.total_quantity || '0');
                    break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [ic_products, search, selectedFilters, sortField, sortOrder]);

    // Add useEffect for window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Modify relevantStocks to use isMobile state
    const relevantStocks = useMemo(() => {
        if (isMobile) {
            return filteredStock;
        }
        return filteredStock.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    }, [filteredStock, currentPage, isMobile]);

    const visibleStocks = `${startIndex}-${endIndex > filteredStock.length ? filteredStock.length:endIndex} of ${filteredStock.length}`;
    const allChecked = checkedStocks.length === relevantStocks.length && relevantStocks.length > 0;
    const setAllChecked = (checked: boolean) => {
        if(checked)
            setCheckedStocks(relevantStocks.map(stock => stock.id));
        else
            setCheckedStocks([]);
    }
    const setChecked = (checked: boolean, id: string) => {
        if(checked)
            setCheckedStocks([...checkedStocks, id]);
        else
            setCheckedStocks(checkedStocks.filter(stockId => stockId !== id));
    }
    const handlePageChange = (page: number) => {
        if(page < 1)
            setCurrentPage(1);
        else
            setCurrentPage(page);
    }

    const handleFilterToggle = (productName: string) => {
        const newFilters = selectedFilters.includes(productName)
            ? selectedFilters.filter(name => name !== productName)
            : [...selectedFilters, productName];
        
        setSelectedFilters(newFilters);
        localStorage.setItem(`filters_${window.location.pathname}`, JSON.stringify(newFilters));
    };

    const handleSelectAllFilters = (checked: boolean) => {
        const newFilters = checked ? uniqueProductNames : [];
        setSelectedFilters(newFilters);
        localStorage.setItem(`filters_${window.location.pathname}`, JSON.stringify(newFilters));
    };

    const uniqueProductNames = useMemo(() => {
        return Array.from(new Set(ic_products.map(product => product.name))) as string[];
    }, [ic_products]);

    // Add ref for the filter container
    const filterContainerRef = useRef<HTMLDivElement>(null);

    // Add useEffect for handling clicks outside the filter popup
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterContainerRef.current && !filterContainerRef.current.contains(event.target as Node)) {
                setShowFilters(false);
            }
        };

        if (showFilters) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilters, setShowFilters]);

    // Add sort handler
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleStockUpdate = (id: string, field: string, value: number, destinationShop: string, destination: string) => {
        const stock = relevantStocks.find(s => s.id === id);
        const newStock = {
            ...stock,
            [field]: value,
            checked: undefined,
            setChecked: undefined,
            destination_shop_id: undefined,
            has_been_consumed: !(destination === StockDestination.Trash)
        };
        if(destinationShop && destination === StockDestination.OtherShop)
            newStock.destination_shop_id = destinationShop;
        dispatch(updateICProductsByShop(selectedShop, id, newStock));
    };

    const MobileStockItem = ({ stock }) => {
        const originalProduct = ic_products.find(p => p.id === stock.id);
        return (
            <div className="stock-table__table__mobile-item">
                <div className="stock-table__table__mobile-item__info">
                    <div className="stock-table__table__mobile-item__info__name">
                        {stock.name}
                    </div>
                    <div className="stock-table__table__mobile-item__info__total">
                        Total: {stock.total_quantity}
                    </div>
                </div>
                <div className="stock-table__table__mobile-item__cells">
                    <div className="stock-table__table__mobile-item__cells__column">
                        <div className="stock-table__table__mobile-item__cells__column__header">
                            7L PACK
                        </div>
                        <StockTableCountCell
                            stock={stock.seven_liter}
                            originalValue={originalProduct?.seven_liter}
                            setStock={(value, destinationShop, destination) => handleStockUpdate(stock.id, 'seven_liter', value, destinationShop, destination)}
                            handleStockModify={(value, destinationShop, destination) => handleStockModify(stock.id, 'seven_liter', value, destinationShop, destination)}
                            destinationShop={destinationShop}
                            setDestinationShop={setDestinationShop}
                            externalDestination={stock.destination}
                        />
                    </div>
                    <div className="stock-table__table__mobile-item__cells__column">
                        <div className="stock-table__table__mobile-item__cells__column__header">
                            5L PACK
                        </div>
                        <StockTableCountCell
                            stock={stock?.five_liter}
                            originalValue={originalProduct?.five_liter}
                            setStock={(value, destinationShop, destination) => handleStockUpdate(stock.id, 'five_liter', value, destinationShop, destination)}
                            handleStockModify={(value, destinationShop, destination) => handleStockModify(stock.id, 'five_liter', value, destinationShop, destination)}
                            destinationShop={destinationShop}
                            setDestinationShop={setDestinationShop}
                            externalDestination={stock.destination}
                        />
                    </div>
                    <div className="stock-table__table__mobile-item__cells__column">
                        <div className="stock-table__table__mobile-item__cells__column__header">
                            3L PACK
                        </div>
                        <StockTableCountCell
                            stock={stock.three_liter}
                            originalValue={originalProduct?.three_liter}
                            setStock={(value, destinationShop, destination) => handleStockUpdate(stock.id, 'three_liter', value, destinationShop, destination)}
                            handleStockModify={(value, destinationShop, destination) => handleStockModify(stock.id, 'three_liter', value, destinationShop, destination)}
                            destinationShop={destinationShop}
                            setDestinationShop={setDestinationShop}
                            externalDestination={stock.destination}
                        />
                    </div>
                </div>
            </div>
        );
    };
    const renderUpperControls = () => (
        <div className="stock-table__upper">
            <span>Edit columns</span>
            <div className="stock-table__upper__controls">
                <span className="p-inputgroup">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-search"></i>
                    </span>
                    <InputText 
                        placeholder="Search..." 
                        className="stock-table__upper__controls__filter-button" 
                        onChange={(e) => setSearch(e.target.value)} 
                        value={search}
                    />
                </span>
                <div className="stock-table__filter-container">
                    <Button 
                        onClick={() => setShowFilters(!showFilters)} 
                        icon="pi pi-filter" 
                        label={'Filters'} 
                        className={`stock-table__upper__controls__filter-button ${showFilters ? 'p-button-highlighted' : ''} ${selectedFilters.length > 0 ? 'filters-active' : ''}`} 
                    />
                </div>
                <Button onClick={onShowModifications} icon={bulkLoading ? "pi pi-spinner pi-spin" : "pi pi-save"} 
                    iconPos='left' disabled={!hasModifications() || bulkLoading} label={'Update All'} className="stock-table__upper__controls__add-button" />
                {userAllowedToEdit && <Button onClick={() => props.setNewFlavorsPopup(true)} icon="pi pi-plus" iconPos='left' label={'Add flavors'} className="stock-table__upper__controls__add-button" />}
            </div>
        </div>
    );
    return (
        <div className="stock-table">
            {showUpperControls && renderUpperControls()}
            <div className="stock-table__filter-container">
                {showFilters && (
                    <>
                        <div 
                            className="stock-table__filter-backdrop visible" 
                            onClick={() => setShowFilters(false)}
                        />
                        <div className="stock-table__filters-popup" ref={filterContainerRef}>
                            <div className="stock-table__filters-popup__item">
                                <Checkbox 
                                    checked={selectedFilters.length === uniqueProductNames.length}
                                    onChange={(e) => handleSelectAllFilters(e.checked)}
                                    id="filter-select-all"
                                />
                                <label htmlFor="filter-select-all">Select All</label>
                            </div>
                            {uniqueProductNames.map(name => (
                                <div key={name} className="stock-table__filters-popup__item">
                                    <Checkbox 
                                        checked={selectedFilters.includes(name)}
                                        onChange={() => handleFilterToggle(name)}
                                        id={`filter-${name}`}
                                    />
                                    <label htmlFor={`filter-${name}`}>{name}</label>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <div className="stock-table__table">
                {!isMobile ? (
                    <>
                        <div className="stock-table__table__header">
                            <Checkbox onChange={e => setAllChecked(e.checked)} checked={allChecked}></Checkbox>
                            <div 
                                onClick={() => handleSort('name')} 
                                className="sortable-header"
                            >
                                FLAVORS {sortField === 'name' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('seven_liter')} 
                                className="sortable-header"
                            >
                                7L PACK {sortField === 'seven_liter' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('five_liter')} 
                                className="sortable-header"
                            >
                                5L PACK {sortField === 'five_liter' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('three_liter')} 
                                className="sortable-header"
                            >
                                3L PACK {sortField === 'three_liter' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('total_quantity')} 
                                className="sortable-header"
                            >
                                TOTAL {sortField === 'total_quantity' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                        </div>
                        {relevantStocks.map((stock) => (
                            <StockTableRow 
                                key={stock.id} 
                                {...stock} 
                                checked={checkedStocks.includes(stock.id)} 
                                setChecked={setChecked}
                                setProductIdToDelete={props.setProductIdToDelete}
                                setWordToConfirm={props.setWordToConfirm}
                                setProductToEdit={props.setProductToEdit}
                                handleStockModify={handleStockModify}
                            />
                        ))}
                    </>
                ) : (
                    modifiedProducts.map((stock) => (
                        <MobileStockItem
                            key={stock.id}
                            stock={stock}
                        />
                    ))
                )}
            </div>
            <div className="stock-table__lower-controls">
            <div className="stock-table__lower-controls__rows-per-page">{`Rows per page: ${itemsPerPage}`}</div>
                <div className="stock-table__lower-controls__visible-rows">{visibleStocks}</div>
                <Button onClick={() => handlePageChange(currentPage - 1)} icon="pi pi-arrow-left" label={'Previous'} className={`stock-table__lower-controls__button ${currentPage===1 ? 'disabled' : ''}`} />
                <Button onClick={() => handlePageChange(currentPage + 1)} icon="pi pi-arrow-right" iconPos='right' label={'Next'} className="stock-table__lower-controls__button" />
            </div>
        </div>
    );
}

export default StockTable;